import React from 'react';
import cn from 'classnames';

export interface RadioProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'checked'
> {
  selected?: boolean;
  disabled?: boolean;
  error?: boolean;
  correct?: boolean;
  label: string;
  className?: string;
  onChange?: (value: string) => void;
}

export const Radio: React.FC<RadioProps> = ({
  selected = false,
  disabled = false,
  error = false,
  correct = false,
  label,
  id: inputId,
  value: inputValue,
  name: groupName,
  className,
  onChange,
  ...props
}) => {
  const wrapperClasses = cn(
    'group inline-flex items-center gap-3 w-fit',
    'pt-1 pb-1 pl-1.5 pr-3 rounded',
    {
      'cursor-pointer': !disabled,
      'cursor-default': disabled,
      // selected + correct: teal/aqua feedback background
      'bg-ds-bg-surface-soft-5': selected && correct && !disabled,
      // selected only
      'bg-ds-bg-surface-soft-4': selected && !correct && !error && !disabled,
      // selected + error
      'bg-ds-bg-feedback-soft-error': selected && error && !disabled,
      'hover:bg-ds-bg-surface-soft-4': !selected && !error && !disabled,
    },
    className,
  );

  const radioClasses = cn(
    'w-5 h-5 border-1 transition-all duration-200 appearance-none rounded-full',
    {
      // selected + error: thick red border
      'border-[6px] border-ds-bg-feedback-error bg-ds-bg-surface-soft-1':
        selected && error && !disabled,
      // error only
      'border-ds-bg-feedback-error bg-ds-bg-feedback-soft-error': error && !selected && !disabled,
      //disabled
      'bg-ds-bg-surface-soft-3 border-ds-border-soft-3': disabled,
      // selected (covers both plain selected and selected+correct — same circle style)
      'border-[6px] border-ds-bg-surface-2 bg-ds-bg-surface-soft-1':
        selected && !error && !disabled,
      //hover
      'group-hover:border-ds-border-2 border-ds-border-3': !selected && !error && !disabled,
    },
  );

  const labelClasses = cn('text-ds-text', {
    'font-semibold': selected,
    'opacity-40': disabled,
  });

  return (
    <label className={wrapperClasses}>
      <input
        id={inputId}
        checked={selected}
        name={groupName}
        value={inputValue}
        type="radio"
        disabled={disabled}
        onChange={() => {
          if (!disabled && inputValue !== undefined) {
            onChange?.(String(inputValue));
          }
        }}
        className={radioClasses}
        {...props}
      />

      <span className={labelClasses}>{label}</span>
    </label>
  );
};

export default Radio;
